<script lang="ts">
import { defineComponent, inject, reactive, ref, computed, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import SectionHeadingForHome from '@/components/heading/SectionHeadingForHome.vue'
import ContentsCard from '@/components/contents/ContentsCard.vue'
import LayoutSolutionContentsCards from '@/components/solutions/LayoutSolutionContentsCards.vue'
import LayoutContentsCardsCols from '@/components/contents/LayoutContentsCardsCols.vue'
import LayoutContentsCardsRows from '@/components/contents/LayoutContentsCardsRows.vue'
import LayoutContentsCardsForHome from '@/components/contents/LayoutContentsCardsForHome.vue'
import FooterAdDocumentBanner from '@/components/banner/FooterAdDocumentBanner.vue'
import ModalProfileForm from '@/components/lead/ModalProfileForm.vue'
import stateInitializer from '@/models/stateInitializer'
import leadGen from '@/models/leadGen'
import { documentsPath, themePath } from '@/generated/railsRoutes'

const CONTENT_LAYOUT_TYPES = {
  REPORT_ONLY: 'REPORT_ONLY',
  ARTICLE_ONLY: 'ARTICLE_ONLY',
  REPORT_AND_ARTICLE: 'REPORT_AND_ARTICLE',
} as const

export default defineComponent({
  name: 'IndexMain',

  components: {
    SectionHeadingForHome,
    ContentsCard,
    LayoutSolutionContentsCards,
    LayoutContentsCardsCols,
    LayoutContentsCardsRows,
    LayoutContentsCardsForHome,
    FooterAdDocumentBanner,
    ModalProfileForm,
  },

  setup() {
    // constants
    const THEMES = inject<Record<string, string>>('THEMES') || {}

    // store
    const userStore = useUserStore()

    interface State {
      documents: ContentItem[],
      themes: Array<{
        id: number,
        contents: ContentItem[],
        latestReport?: ReportItem,
        layoutType: typeof CONTENT_LAYOUT_TYPES[keyof typeof CONTENT_LAYOUT_TYPES],
      }>,
      profile: {
        form: Form,
        input: {
          profile: {
            lastName: InputText,
            firstName: InputText,
            lastNameKana: InputText,
            firstNameKana: InputText,
          },

          career: {
            careerId: InputNumber,
            companyName: InputText,
            corporateZipcode: InputText,
            corporateAddress1: InputText,
            corporateAddress2: InputText,
            corporateNumber: InputText,
            corporateEmployeeNum: InputNumber,
            industry: InputText,
            scale: InputText,
            profession: InputText,
            positionLevel: InputText,
            departmentAndPosition: InputText,
          },
        },
      },
    }

    const state: State = reactive({
      documents: [],
      themes: [],
      profile: {
        form: {
          name: '',
          action: '',
          method: 'put',
        },
        input: {
          profile: {
            lastName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            firstName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            lastNameKana: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                kana: true,
              },
            },
            firstNameKana: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                kana: true,
              },
            },
          },
          career: {
            careerId: {
              value: undefined,
              name: '',
            },
            companyName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            corporateZipcode: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateAddress1: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateAddress2: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateNumber: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateEmployeeNum: {
              value: undefined,
              name: '',
              validationScheme: {},
            },
            industry: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            scale: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            profession: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            positionLevel: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            departmentAndPosition: {
              value: '',
              name: '',
              validationScheme: {},
            },
          },
        },
      },
    })

    stateInitializer(state, 'main')

    // プロフィール入力モーダルの表示制御フラグ
    const isShowProfileModal = ref(false)

    const userLeadInfoForProfileModal = computed<UserLeadInfo>(() => {
      return {
        ...state.profile.input.profile,
        ...state.profile.input.career,
      }
    })

    // profile＋career を渡すことで hasUserLeadInfo が「必須項目がすべて入力済みか」を判定する
    const {
      // data
      hasUserLeadInfo,

      // methods
      selectCompanyLeadInfo,
      inputCompanyName,
    } = leadGen(userLeadInfoForProfileModal.value)

    // ログイン済み かつ 必須項目に未入力があれば、初期表示でモーダルを開く（旧 _profile_modal 踏襲）
    // userStore.context は app.mount 後の assignStoreData でハイドレートされるため、
    // onMounted の同期読みでは isSignedIn がまだ false。変化を watch して判定する。
    watch(() => userStore.context.isSignedIn, (isSignedIn) => {
      if (isSignedIn && !hasUserLeadInfo.value) {
        isShowProfileModal.value = true
      }
    }, { immediate: true })

    return {
      // constants
      THEMES,
      CONTENT_LAYOUT_TYPES,

      // routes
      documentsPath,
      themePath,

      // data
      state,
      isShowProfileModal,

      // computed
      userLeadInfoForProfileModal,

      // methods
      selectCompanyLeadInfo,
      inputCompanyName,
    }
  },
})
</script>

<template>
  <section class="section">
    <section
      v-if="!!state.documents.length"
      class="section">
      <SectionHeadingForHome>
        お役立ち資料
      </SectionHeadingForHome>

      <LayoutSolutionContentsCards
        :items="state.documents"
        :cols="{ pc: 4, sp: 'fixed' }"
        :horizontal="{
          pc: false,
          sp: false,
        }"
        :image-options="{
          width: {
            pc: 148,
            sp: 187,
          },
          height: {
            pc: 78,
            sp: 94,
          },
        }" />

      <p class="p u-text__small u-text__right u-margin__larger">
        <a
          class="a u-action__forward"
          :href="documentsPath()">
          お役立ち資料をもっと見る
        </a>
      </p>
    </section>

    <section
      v-for="theme in state.themes"
      :key="theme.id"
      class="section">
      <SectionHeadingForHome>
        {{ THEMES[theme.id] }}
      </SectionHeadingForHome>

      <template v-if="theme.layoutType === CONTENT_LAYOUT_TYPES.REPORT_AND_ARTICLE">
        <LayoutContentsCardsForHome :items="[theme.latestReport, ...theme.contents]" />
      </template>
      <template v-else-if="theme.layoutType === CONTENT_LAYOUT_TYPES.REPORT_ONLY">
        <LayoutContentsCardsCols
          v-if="theme.contents.length"
          :items="theme.contents"
          :cols="{ pc: 4, sp: 1 }"
          :horizontal="{
            pc: false,
            sp: false,
          }"
          :heading-options="{ maxLine: 2 }"
          :image-options="{
            width: {
              pc: 148,
              sp: 187,
            },
            height: {
              pc: 78,
              sp: 98,
            },
          }" />
      </template>
      <template v-else-if="theme.layoutType === CONTENT_LAYOUT_TYPES.ARTICLE_ONLY">
        <LayoutContentsCardsCols
          v-if="theme.contents.length"
          :items="theme.contents"
          :cols="{ pc: 2, sp: 1 }"
          :horizontal="{
            pc: true,
            sp: true,
          }"
          :heading-options="{ maxLine: 2 }"
          :image-options="{
            width: {
              pc: 148,
              sp: 187,
            },
            height: {
              pc: 78,
              sp: 98,
            },
          }" />
      </template>

      <p class="p u-text__small u-text__right u-margin__larger">
        <a
          class="a u-action__forward"
          :href="themePath({ id: theme.id })">
          {{ THEMES[theme.id] }}をもっと見る
        </a>
      </p>
    </section>

    <FooterAdDocumentBanner />

    <ModalProfileForm
      :form="state.profile.form"
      :is-show="isShowProfileModal"
      :namespace="state.profile.form.name"
      :user-lead-info="userLeadInfoForProfileModal"
      @update:last-name="state.profile.input.profile.lastName.value = $event"
      @update:first-name="state.profile.input.profile.firstName.value = $event"
      @update:last-name-kana="state.profile.input.profile.lastNameKana.value = $event"
      @update:first-name-kana="state.profile.input.profile.firstNameKana.value = $event"
      @update:company-name="inputCompanyName($event)"
      @select:company-lead-info="selectCompanyLeadInfo($event)"
      @reset:corporate-zipcode="state.profile.input.career.corporateZipcode.value = $event"
      @update:industry="state.profile.input.career.industry.value = $event"
      @update:scale="state.profile.input.career.scale.value = $event"
      @update:profession="state.profile.input.career.profession.value = $event"
      @update:position-level="state.profile.input.career.positionLevel.value = $event"
      @update:department-and-position="state.profile.input.career.departmentAndPosition.value = $event" />
  </section>
</template>
